import { z } from 'zod';
import { ConstraintSchema, constraintFields } from '@/lib/account-constraints';
import type { AccountConstraint } from '@/types/schemas';

const requiredText = (message: string) => z.string().trim().min(1, message);

const requiredNumberGreaterThanZero = (message: string) =>
  z.preprocess(
    value => {
      if (value === '' || value === null || value === undefined) return undefined;
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? undefined : numericValue;
    },
    z
      .number({
        required_error: message,
        invalid_type_error: message
      })
      .gt(0, message)
  );

export const searchSchema = z.object({
  // BasicInfoTab fields
  ngay_ct1: z.string().optional(),
  ngay_ct2: z.string().optional(),
  so_ct1: z.string().optional().nullable(),
  so_ct2: z.string().optional().nullable(),
  ma_kh: z.string().optional(),
  tk: z.string().optional(),
  ma_vt: z.string().optional(),
  ma_kho: z.string().optional(),
  ma_unit: z.string().optional(),
  ma_lo: z.string().optional(),
  ma_vi_tri: z.string().optional(),
  dien_giai: z.string().optional().nullable(),
  status: z.string().optional()
});

export type SearchFormValues = z.infer<typeof searchSchema>;
export const initialSearchValues: SearchFormValues = {
  status: 'ALL'
};

const nullableString = z.string().optional().nullable();
const nullableNumber = z.coerce.number().optional().nullable();
const nullableBoolean = z.boolean().optional().nullable();
const nullableDate = z.union([z.string(), z.date()]).optional().nullable();

const dimensionSchema = {
  ma_bp: nullableString,
  ma_vv: nullableString,
  ma_hd: nullableString,
  ma_dtt: nullableString,
  ma_ku: nullableString,
  ma_phi: nullableString,
  ma_sp: nullableString,
  ma_lsx: nullableString,
  ma_cp0: nullableString
};

const chiTietHoaDonSchema = z
  .object({
    uuid: nullableString,
    line: nullableNumber,
    ma_vt: nullableString,
    ma_kho: nullableString,
    dvt: nullableString,
    so_luong: nullableNumber,
    gia_nt0: nullableNumber,
    tien_nt0: nullableNumber,
    cp_nt: nullableNumber,
    gia_nt: nullableNumber,
    gia_ton: nullableNumber,
    gia_ton_nt: nullableNumber,
    ma_thue: nullableString,
    thue_nt: nullableNumber,
    tl_ck: nullableNumber,
    tien_tck: nullableNumber,
    tien_tck_nt: nullableNumber,
    ck_nt: nullableNumber,
    ck: nullableNumber,
    tk_cpxt: nullableString,
    tk_vt: nullableString,
    px_dd: nullableBoolean,
    id_dh: nullableString,
    line_dh: nullableString,
    so_ct_dh: nullableString,
    so_ct_hd4: nullableString,
    line_dh4: nullableString,
    ...dimensionSchema
  })
  .passthrough();

const chiPhiHoaDonSchema = z
  .object({
    uuid: nullableString,
    line: nullableNumber,
    ma_cp: nullableString,
    ten_cp: nullableString,
    tien_cp_nt: nullableNumber,
    tien_cp: nullableNumber,
    ma_kh: nullableString,
    ten_kh: nullableString,
    tk: nullableString,
    ten_tk: nullableString,
    loai_pb: nullableString,
    ...dimensionSchema
  })
  .passthrough();

const chiPhiChiTietHoaDonSchema = z
  .object({
    uuid: nullableString,
    line: nullableNumber,
    ma_cp: nullableString,
    ten_cp: nullableString,
    ma_vt: nullableString,
    ten_vt: nullableString,
    tien_cp_nt: nullableNumber,
    tien_cp: nullableNumber,
    line_vt: nullableNumber,
    line_cp: nullableNumber,
    ...dimensionSchema
  })
  .passthrough();

const thueHoaDonSchema = z
  .object({
    uuid: nullableString,
    line: nullableNumber,
    so_ct0: nullableString,
    so_ct2: nullableString,
    ngay_ct0: nullableDate,
    ma_thue: nullableString,
    thue_suat: nullableNumber,
    ma_mau_ct: nullableString,
    ma_mau_bc: nullableString,
    ma_tc_thue: nullableString,
    ma_kh: nullableString,
    ten_kh_thue: nullableString,
    dia_chi: nullableString,
    ma_so_thue: nullableString,
    ten_vt_thue: nullableString,
    t_tien_nt: nullableNumber,
    t_tien: nullableNumber,
    tk_thue_no: nullableString,
    ten_tk_thue_no: nullableString,
    t_thue_nt: nullableNumber,
    t_thue: nullableNumber,
    ma_kh9: nullableString,
    ma_tt: nullableString,
    ghi_chu: nullableString,
    so_ct_dh: nullableString,
    ...dimensionSchema
  })
  .passthrough();

const baseSchema = z.object({
  hdmh_yn: z.boolean().optional(),
  pn_yn: z.boolean().optional(),
  pc_tao_yn: z.boolean().optional(),
  xt_yn: z.boolean().optional(),
  ck_yn: z.boolean().optional(),
  loai_ck: z.string().optional(),
  ck_tl_nt: z.coerce.number().optional(),
  ten_kh: z.string().optional().nullable(),
  ma_so_thue: z.string().optional().nullable(),
  dia_chi: z.string().optional().nullable(),
  ong_ba: z.coerce.string().optional(),
  e_mail: z.string().optional().nullable(),
  tk: z.string().optional().nullable(),
  dien_giai: z.string().optional().nullable(),
  unit_id: z.string().optional(),
  ma_kh: requiredText('Mã nhà cung cấp bắt buộc nhập'),
  so_ct: z.string().optional(),
  ngay_ct: z.string().optional(),
  ngay_lct: z.string().optional(),
  so_ct0: z.string().optional().nullable(),
  ngay_ct0: z.string().optional(),
  so_ct2: z.string().optional().nullable(),
  so_pn: z.string().optional(),
  ngay_pn: z.string().optional(),
  i_so_pn: z.string().optional().nullable(),
  ma_nt: z.string().optional(),
  ty_gia: z.coerce.number().optional(),
  status: z.string().optional(),
  transfer_yn: z.boolean().optional(),
  ma_gd: z.string().optional(),
  phieu_chi_uuids: z.array(z.string()).optional(),
  phieu_nhap_kho_uuid: z.string().optional(),
  thue_hoa_don: z.array(thueHoaDonSchema).optional(),
  chi_phi_chi_tiet_hoa_don: z.array(chiPhiChiTietHoaDonSchema).optional(),
  chi_phi_hoa_don: z.array(chiPhiHoaDonSchema).optional(),
  chi_tiet_hoa_don: z
    .array(
      z.object({
        ma_vt: requiredText('Mã sản phẩm bắt buộc nhập'),
        ma_kho: requiredText('Mã kho bắt buộc nhập'),
        so_luong: requiredNumberGreaterThanZero('Số lượng phải lớn hơn 0'),
        ...constraintFields
      })
    )
    .optional()
});

export const formSchema = (constraints: AccountConstraint[] = []) =>
  ConstraintSchema(baseSchema, constraints, { detailFieldName: 'chi_tiet_hoa_don' });
export type FormSchema = z.infer<typeof baseSchema>;

export const initialFormValues: FormSchema = {
  hdmh_yn: true,
  pn_yn: true,
  pc_tao_yn: false,
  xt_yn: false,
  ck_yn: false,
  loai_ck: '1',
  ck_tl_nt: 0.0,
  ten_kh: '',
  ma_so_thue: '',
  dia_chi: '',
  ong_ba: '',
  e_mail: '',
  tk: '',
  dien_giai: '',
  unit_id: '',
  ma_kh: '',
  so_ct: '',
  ngay_ct: new Date().toISOString().split('T')[0],
  ngay_lct: new Date().toISOString().split('T')[0],
  so_ct0: '',
  ngay_ct0: new Date().toISOString().split('T')[0],
  so_ct2: '',
  so_pn: '',
  ngay_pn: new Date().toISOString().split('T')[0],
  i_so_pn: '',
  ma_nt: '',
  ty_gia: 1,
  status: '5',
  transfer_yn: false,
  ma_gd: '',
  phieu_chi_uuids: [],
  phieu_nhap_kho_uuid: '',
  thue_hoa_don: [],
  chi_phi_chi_tiet_hoa_don: [],
  chi_phi_hoa_don: []
};
